<template>
  <v-container grid-list-xl fluid>
    <v-layout row wrap>
      <v-overlay :value="pageLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <v-flex v-if="!pageLoading">
        <filter-contacts
          :default-search-params="searchParams"
          :is-component-loading="isRequestingNow"
          @filtering="onFilter"
          @download-excel="onDownloadExcel"
          @send-email-excel="onSendEmailExcel"
        ></filter-contacts>
        <table-paginated-contacts
          :is-component-loading="isRequestingNow"
          :paginated-object="paginatedObject"
          @edit-item="onPaginatedItemEdit"
          @delete-item="onPaginatedItemDelete"
          @preview-item="onPreviewItem"

          @edit-address="onAddressEdit"
          @delete-address="onAddressDelete"
        ></table-paginated-contacts>
        <pagination-listing
          :pagination-data="paginationData"
          @go-prev="changePage('-')"
          @go-next="changePage('+')"
        ></pagination-listing>
      </v-flex>
      <modal-loading :is-show="isTableLoading"></modal-loading>
      <!-- Edit Modal -->
      <edit-modal-contact
        :allow-add-address="true"
        :item="currentEditableItem"
        :is-show-dialog="isShowUserDialog"
        :is-modal-loading="isLoadingUserDialog"
        :action-label="userDialogActionLabel"
        :errors="errorsUserDialog"
        @on-close="onCloseUserDialog"
        @on-action="onActionUserDialog"
        @on-add-address="onAddAddress"
        @edit-address="onAddressEdit"
      />
      <v-btn
        color="pink"
        dark
        fixed
        bottom
        right
        fab
        @click="currentEditableItem = {}; isShowUserDialog = true"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
      <!-- End: Edit Modal -->
      <!-- Edit Modal Address -->
      <edit-modal-address
        :contact="currentEditableItem"
        :item="currentEditableAddress"
        :is-show-dialog="isShowAddressDialog"
        :is-modal-loading="isLoadingAddressDialog"
        :action-label="addressDialogActionLabel"
        :errors="errorsAddressDialog"
        @on-close="onCloseAddressDialog"
        @on-action="onActionAddressDialog"
      />
      <!-- End: Edit Modal Address -->
      <accept-dialog
        :is-open-dialog="isShowDeleteDialog"
        @on-close="isShowDeleteDialog = false"
        @on-accept="onAcceptDeletion"
      >
        <div class="pt-6">
          {{acceptModalText}}
        </div>
      </accept-dialog>
    </v-layout>
    <preview-popup
      :is-show-popup="previewPopup.show"
      :title="previewPopup.title"
      @on-close="previewPopup.show = false"
    >
      {{previewPopup.content}}
    </preview-popup>
  </v-container>
</template>

<script>
import Vue from 'vue';
import _ from 'lodash';
/* constants */
import { PER_PAGE } from '@/constants/listing';
import { DESC } from '@/constants/sorting';

/* utils */
import { deepMerge, prepareValidationMessages, strReplaceAll } from '@/utils/index';
/* proxies */
import ContactProxy from '@/proxies/Contact/ContactProxy';
/* mixins */
import defaultListingMixin from '@/mixins/listing/defaultListingMixin';
import updatePageContentMixin from '@/mixins/updatePageContentMixin';
import paginationMixin from '@/mixins/listing/paginationMixin';
import downloadReport from '@/mixins/reports/downloadReport';
import sendEmailReport from '@/mixins/reports/sendEmailReport';
import addressDialog from '@/mixins/contacts/addressDialog';
/* components */
import ModalLoading from '@/components/loaders/ModalLoading.vue';
import PaginationListing from '@/components/listings/PaginationListing.vue';
import AcceptDialog from '@/components/AcceptDialog.vue';
import FilterContacts from '@/components/listings/FilterContacts.vue';
import TablePaginatedContacts from '@/components/listings/TablePaginatedContacts.vue';
import PreviewPopup from '@/components/PreviewPopup.vue';
import EditModalContact from '@/components/listings/EditModalContact.vue';
import EditModalAddress from '@/components/listings/EditModalAddress.vue';
import AddressProxy from '@/proxies/Contact/AddressProxy';

export default {
  name: 'ContactsIndex',
  components: {
    ModalLoading,
    AcceptDialog,
    PaginationListing,
    FilterContacts,
    TablePaginatedContacts,
    PreviewPopup,
    EditModalContact,
    EditModalAddress,
  },
  mixins: [
    defaultListingMixin,
    updatePageContentMixin,
    paginationMixin,
    downloadReport,
    sendEmailReport,
    addressDialog,
  ],
  data() {
    return {
      reportMethodName: 'contactsListingReport',
      /* Page management */
      pageLoading: false,
      /* Create - Edit user */
      isLoadingUserDialog: false,
      errorsUserDialog: {},
      isShowUserDialog: false,
      currentEditableItem: {},
      /* Delete user */
      isShowDeleteDialog: false,
      /* Listing management */
      isTableLoading: false,
      searchParams: {
        search: '',
        source: null,
        subscription: null,
        region: null,
        sortByOrder: null,
        direction: DESC,
        last_order_status: null,
        last_order_days: null,
        last_order_from: null,
        last_order_to: null,
        subscription_expiring_days: null,
        is_commerce: null,
        premises_type: null,
        page: {
          number: 1,
          size: PER_PAGE,
        },
        app_type: process.env.VUE_APP_TYPE,
      },
      previewPopup: {
        show: false,
        title: '',
        content: '',
      },
    };
  },
  created() {
    this.isTableLoading = true;
    this.fetchData();
  },
  computed: {
    /* edit modal */
    userDialogActionLabel() {
      if (this.isShowUserDialog) {
        return (this.currentEditableItem.name) ?
          this.lang(this.$store, 'sap/listings.global.edit.save') :
          this.lang(this.$store, 'sap/listings.global.edit.create');
      }
      return '';
    },
    /* accept modal */
    acceptModalText() {
      if (!_.isEmpty(this.currentEditableAddress)) {
        return strReplaceAll(this.lang(this.$store, 'sap/listings.global.delete.question'), {
          user: this.currentEditableAddress.formatted,
        });
      }
      return strReplaceAll(this.lang(this.$store, 'sap/listings.global.delete.question'), {
        user: this.currentEditableItem.name,
      });
    },
    /* store */
    account() {
      return this.$store.getters['account/account'];
    },
  },
  methods: {
    fetchData() {
      this.isRequestingNow = true;
      new ContactProxy()
        .setParameters(this.searchParams)
        .all()
        .then(response => {
          this.paginatedObject = response.data.data.contacts;
        })
        .catch(error => {
          console.warn(error);
          this.paginatedObject = {};
        })
        .finally(() => {
          this.isTableLoading = false;
          this.isRequestingNow = false;
          this.$store.dispatch('page/finishUpdateCurrentPageContent');
        });
    },
    onPaginatedItemEdit(item) {
      this.$router.push({
        name: 'contacts.show',
        params: { uuid: item.uuid },
      });
    },
    onPaginatedItemDelete(item) {
      this.currentEditableItem = item;
      this.currentEditableAddress = {};
      this.isShowDeleteDialog = true;
    },
    onFilter(event) {
      const newSearchParams = Object.assign({}, event);
      if (_.isString(newSearchParams.source)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.source = [newSearchParams.source];
      }
      if (_.isString(newSearchParams.region)) {
        // eslint-disable-next-line no-param-reassign
        newSearchParams.region = [newSearchParams.region];
      }
      this.searchParams = deepMerge(this.searchParams, deepMerge(newSearchParams, {
        page: {
          number: 1,
        },
      }));
    },
    onCloseUserDialog() {
      this.isShowUserDialog = false;
    },
    onActionUserDialog(item) {
      if (_.isObject(item)) {
        const updatedData = {
          name: item.name,
          phone: item.phone,
          phone2: item.phone2,
          phone_note: item.phone_note,
          phone2_note: item.phone2_note,
          note: item.note,
          additional_info: item.additional_info,
          source: item.source,
          use_ukrainian: item.use_ukrainian,
          use_english: item.use_english,
          is_viber: item.is_viber,
          black_listed: item.black_listed,
        };
        this.isLoadingUserDialog = true;
        let client = new ContactProxy();
        if (this.currentEditableItem.uuid) {
          client = client.update(this.currentEditableItem.uuid, updatedData);
        } else {
          client = client.create(updatedData);
        }

        client.then(response => {
          this.currentEditableItem = response.data.data.contact;
          this.isShowUserDialog = false;
          if (item.addAddress) {
            this.onAddAddress(this.currentEditableItem);
          }
          this.$store.dispatch('page/updateCurrentPageContent');
        })
          .catch(response => {
            this.errorsUserDialog = prepareValidationMessages(response.data.data);
          })
          .finally(() => {
            this.isLoadingUserDialog = false;
          });
      }
    },
    onPreviewItem(item) {
      this.previewPopup.title = this.lang(this.$store, 'sap/listings.global.additional_info');
      this.previewPopup.content = item.additional_info;
      Vue.set(this.previewPopup, 'show', true);
    },
    requestDeleteUser() {
      this.isRequestingNow = true;
      new ContactProxy().destroy(this.currentEditableItem.uuid)
        .catch(response => {
          console.error(response);
          alert(`Error: ${response.status}`);
          this.isRequestingNow = false;
        })
        .then(() => {
          this.fetchData();
        });
    },
    /* Address */
    onAddAddress(item) {
      this.isShowUserDialog = false;
      this.currentEditableItem = item;
      this.currentEditableAddress = {};
      this.isShowAddressDialog = true;
    },
    onAddressEdit({ item, address }) {
      this.currentEditableItem = item;
      this.currentEditableAddress = address;
      this.isShowAddressDialog = true;
    },
    onAddressDelete({ item, address }) {
      this.currentEditableItem = item;
      this.currentEditableAddress = address;
      this.isShowDeleteDialog = true;
    },
    requestDeleteAddress() {
      this.isRequestingNow = true;
      new AddressProxy().destroy(this.currentEditableAddress.uuid)
        .catch(response => {
          console.error(response);
          alert(`Error: ${response.status}`);
          this.isRequestingNow = false;
        })
        .then(() => {
          this.fetchData();
        });
    },
    /* General */
    onAcceptDeletion() {
      if (!_.isEmpty(this.currentEditableAddress)) {
        this.requestDeleteAddress();
      } else {
        this.requestDeleteUser();
      }
    },
  },
};
</script>
